import Controller from '@ember/controller';
import { action } from '@ember/object';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import {
  Radar,
  default_options,
  default_styles,
} from 'scaleflow/utils/radar/radar';
import { get_color_from_class } from 'scaleflow/utils/radar/color_map';

export default class RoadmapRadarController extends Controller {
  @service('beta-toggle') betaService;
  @service router;
  @service store;
  @service organisation;

  @service initiativeSlideover;

  @tracked status = ['ACTIVE', 'DISCOVERY', 'READY', 'READY FOR PLANNING'];
  @tracked themes = [];
  @tracked teams = [];
  @tracked showFilterSlideover = false;

  queryParams = [
    'grouping',
    'status',
    'themes',
    'sources',
    'product_managers',
    'teams',
  ];
  @tracked view = '';
  @tracked grouping = null;
  @tracked sources = [];
  @tracked product_managers = [];
  @tracked themeOptions = [];

  radarInstance = null;

  get teamOptions() {
    return this.store.peekAll('team');
  }

  get radarData() {
    const data_object = {
      timelines: this.timelines,
      initiatives: this.initiatives,
      sections: this.sections,
      color_map: this.color_map,
      markers: [],
    };

    return data_object;
  }

  get options() {
    const options = default_options;
    options.initiatives.onClick = (initiative) =>
      this.onInitiativeSelect(initiative.id);
    options.markers = {
      today: false,
      center: true,
    };
    return options;
  }

  get styles() {
    return default_styles;
  }

  get sections() {
    return [
      {
        name: 'Customer Request',
        weight: this.organisation.customer_request_capacity,
        id: 'CUSTOMER REQUEST CURRENT',
        color: '#EFF6FF',
      },
      {
        name: 'Operations & Maintenance',
        weight: this.organisation.operations_capacity,
        id: 'OPERATIONS AND MAINTENANCE',
        color: '#ECFDF5',
      },
      {
        name: 'Vision',
        weight: this.organisation.vision_capacity,
        id: 'VISION',
        color: '#FFF7ED',
      },
    ];
  }

  get timelines() {
    return [
      {
        label: 'Now (+3w)',
        date: this.add_weeks_to_date(new Date(), 3),
        capacity: 20,
      },
      {
        label: 'Next (+12w)',
        date: this.add_weeks_to_date(new Date(), 12),
        capacity: 40,
      },
      {
        label: 'Later (+24w)',
        date: this.add_weeks_to_date(new Date(), 24),
        capacity: 80,
      },
    ];
  }

  add_weeks_to_date(date, weeks) {
    const one_week = 7 * 24 * 60 * 60 * 1000;
    const new_date = new Date(date);
    new_date.setTime(date.getTime() + weeks * one_week);
    return new_date;
  }

  get color_map() {
    const color_map = {};
    for (let i = 0; i < this.themeOptions.length; i++) {
      color_map[this.themeOptions[i].id] =
        get_color_from_class(this.themeOptions[i].color) ??
        this.themeOptions[i].color;
    }
    return color_map;
  }

  get initiatives() {
    if (this.status.length === 0) return this.model.initiatives.slice();
    else
      return this.model.initiatives
        .slice()
        .filter((item) => this.status.includes(item.status));
  }

  @action
  onInitiativeSelect(initiative_id) {
    this.initiativeSlideover.open(initiative_id);
  }

  @action
  initiateRadar() {
    const radarData = this.radarData;
    this.radarInstance = new Radar(
      '#now-next-later',
      radarData,
      this.options,
      this.styles,
    );
    this.radarInstance.generate();
  }

  @action
  updateRadar(element) {
    (element ?? document).querySelector('#now-next-later').innerHTML = '';
    this.initiateRadar();
  }

  @action
  onRender() {
    this.initiateRadar();
  }

  @action
  handleStatusSelect(e) {
    if (this.status.includes(e.value)) {
      this.status = this.status.filter((item) => item !== e.value);
    } else {
      const status = this.status;
      status.push(e.value);
      this.status = status;
    }
  }

  @action
  transitionToCreateInitiative() {
    this.router.transitionTo(`/initiatives-create`);
  }

  @action
  toggleFilterSlideover(flag) {
    if (flag !== undefined && typeof flag === 'boolean')
      this.showFilterSlideover = flag;
    else this.showFilterSlideover = !this.showFilterSlideover;
  }

  @action
  onGroupingSelect(e) {
    this.grouping = e.value;
  }

  @action
  onStatusSelect(e) {
    this.status = e;
  }

  @action
  onThemeSelect(e) {
    this.themes = e;
  }

  @action
  onSourceSelect(e) {
    this.sources = e;
  }

  @action
  onPmSelect(e) {
    this.product_managers = e;
  }

  @action
  onTeamSelect(e) {
    this.teams = e;
  }

  @action
  handleFilterDelete(filter) {
    const param = filter.key;
    const filters = this[param];
    if (Array.isArray(filters))
      this[param] = filters.filter((item) => item !== filter.value);
    else this[param] = null;
  }
}
